/**
 * Client-side transport for the ONE REST escape hatch this feature owns
 * (`GET /vapid/public-key`, F22/E17 §5.1). Mirrors
 * `apps/web/src/features/uploads/uploadTransport.ts`'s `resolveUploadUrl`
 * exactly — same-origin derivation with a `VITE_*` override, since the Vite
 * dev server proxies this path to `@xdocs/server` (see this feature's
 * `WIRING.md` for the one dev-proxy line `apps/web/vite.config.ts` needs).
 */

const DEV_VAPID_PORT = 8080;

function deriveDevVapidPublicKeyUrl(): string {
  if (typeof window === 'undefined') return `http://localhost:${DEV_VAPID_PORT}/vapid/public-key`;
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${window.location.host}/vapid/public-key`;
}

export function resolveVapidPublicKeyUrl(): string {
  const configured = import.meta.env.VITE_VAPID_PUBLIC_KEY_URL;
  if (configured && configured.trim().length > 0) return configured;
  return deriveDevVapidPublicKeyUrl();
}

export class VapidTransportError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'VapidTransportError';
  }
}

/** Fetches the server's VAPID public key for `PushManager.subscribe`. Never caches across calls (small, cheap; the HTTP layer's own `Cache-Control` handles reuse). */
export async function fetchVapidPublicKey(fetchImpl: typeof fetch = fetch): Promise<string> {
  let response: Response;
  try {
    response = await fetchImpl(resolveVapidPublicKeyUrl());
  } catch (err) {
    throw new VapidTransportError(`network error fetching VAPID public key: ${err instanceof Error ? err.message : String(err)}`);
  }
  if (!response.ok) {
    throw new VapidTransportError(`GET /vapid/public-key returned ${response.status}`, response.status);
  }
  const body: unknown = await response.json();
  const publicKey = (body as { publicKey?: unknown }).publicKey;
  if (typeof publicKey !== 'string' || publicKey.length === 0) {
    throw new VapidTransportError('GET /vapid/public-key returned an invalid body');
  }
  return publicKey;
}
